import { Skeleton } from '@app/components/various/Skeleton';
import { SkeletonLayout } from '@app/components/various/SkeletonLayout';
import { getOrderDetails } from '@app/ducks';
import { cn } from '@app/utils/cn';
import { useAvailableOrderDetails } from '@app/utils/hooks/useAvailableOrderDetails';
import { isDefined } from '@app/utils/is';
import { isEmpty } from '@app/utils/isEmpty';
import { prop, uniqBy } from 'ramda';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

const GAP_SIZES = [
  { maxWidth: 824, size: 'small' },
  { maxWidth: 864, size: 'medium' },
  { maxWidth: 904, size: 'large' },
] as const;

const getGapSize = (width: number) => {
  const { size = 'xlarge' } = GAP_SIZES.find(({ maxWidth }) => width < maxWidth) ?? {};

  return size;
};

interface Props {
  parentWidth?: number;
}

export const OrderSummary = ({ parentWidth }: Props) => {
  const { t } = useTranslation(['products', 'totals', 'common']);

  const orderDetails = useSelector(getOrderDetails);

  const { orderTotals } = useAvailableOrderDetails();
  const { products, order: orderId } = orderDetails.order;

  const gapSize = React.useMemo(() => {
    if (isDefined(parentWidth)) {
      return getGapSize(parentWidth);
    }

    return undefined;
  }, [parentWidth]);

  const productsCount = React.useMemo(() => {
    return isEmpty(products) ? undefined : uniqBy(prop('product'), products).length;
  }, [products]);

  const totals = React.useMemo(
    () => [
      {
        isHidden: isDefined(parentWidth) && parentWidth < 520,
        title: t('products:product', { count: productsCount }),
        value: productsCount,
      },
      {
        isHidden: isDefined(parentWidth) && parentWidth < 440,
        title: t('common:unit', { count: orderTotals?.itemsCount }),
        value: orderTotals?.itemsCount,
      },
      {
        title: t('totals:subtotal'),
        value: orderTotals?.itemsTotalPrice,
      },
    ],
    [parentWidth, t, productsCount, orderTotals],
  );

  return (
    <dl
      className={cn('flex items-center gap-10 overflow-hidden sm:overflow-visible', {
        'gap-4': gapSize === 'small',
        'gap-8': gapSize === 'medium',
        'gap-9': gapSize === 'large',
        'justify-between': !isDefined(gapSize),
      })}
    >
      {totals
        .filter(({ isHidden = false }) => !isHidden)
        .map(({ title, value }) => (
          <div key={title} className="overflow-hidden sm:overflow-visible">
            <dt className="text-neutral-60 heading-4">{title}</dt>
            <dd className="truncate body-1 sm:overflow-visible">
              <SkeletonLayout isLoading={!isDefined(orderId)} skeleton={<Skeleton type="text" size="large" />}>
                {value}
              </SkeletonLayout>
            </dd>
          </div>
        ))}
    </dl>
  );
};
